<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader && !showFilters"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState"
    :showFloatingButton="showFilters && !applyErrorState"
    :floatingButtonParams="{ icon: 'filter', label: t('filters.filter')}"
    :showFiltersAndErrorState="showFilters && applyErrorState"
    :showFiltersAndEmptyState="showFilters && !getTmxList.length && !applyErrorState"
    @floatingButtonClicked="showFiltersModal = true"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Visible Filters -->
        <consult-filters-wrapper
        v-if="showFilters"
        :filterTags="filterTags"
        :applyButtonDisabled="applyButtonDisabled"
        @applyFilters="applyFiltersHandler(appliedFilters)"
        @removeFilterTag="removeFilterTag"
        @closeAllFilters="closeAllFilters">
			<!-- Double Calendar -->
			<input-double-calendar
			calendarDirection="row"
			:fromDateParams="beginDateParams"
			:toDateParams="endDateParams"
			@inputFromDate="appliedFilters.beginDate = $event"
			@inputToDate="appliedFilters.endDate = $event"/>
        </consult-filters-wrapper>
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && !applyErrorState && getTmxList.length" class="content-wrapper prevent-auto-margins">
                <!-- Table Generic -->
                <table-generic v-if="getTmxList.length"
                :headerData="tableHeaderData"
                :tableStyles="tableStyles"
                :tableData="tableData"
                :hasMore="getTmxListHasMore"
                :isLoadingMore="tableLoadingMore"
                :loadingMoreText="t('transfers.requests.international.tableLoadingMore')"
                @loadMore="loadMoreHandler"
                @rowClicked="tmxClickHandler($event)"/>
            </div>
        </transition>
			<template #empty-state-slot>
				<!-- Empty State -->
				<empty-state-generic
				v-if="!showLoader && !applyErrorState && !getTmxList.length"
				:illustration="emptyStateIlustration"
				:title="emptyStateTitle"
				:description="emptyStateDescription"/>
			</template>
	</wrapper-features-templates-one-column>
    <!-- Detail Modal -->
	<detail-modal
	v-if="showDetailModal"
	:selectedTmxRequest="selectedTmxRequest"
	@hideMe="showDetailModal = false"/>
	<!-- Filter Modal -->
	<filter-modal
	v-if="showFiltersModal"
	:appliedFilters="appliedFilters"
	:currencyList="getTmxCurrencyList"
	:beginDateParams="beginDateParams"
	:endDateParams="endDateParams"
	@hideMe="showFiltersModal = false"
	@applyFilters="applyFiltersHandler($event)"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
/* Generic Components */
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ConsultFiltersWrapper from '@/components/wrappers/ConsultFiltersWrapper'
import InputDoubleCalendar from '@/components/generic/inputs/DoubleCalendar'
import FilterModal from '@/components/features/transfers/requests/international/FilterModal'
import DetailModal from '@/components/features/transfers/requests/international/DetailModal'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { getDate, getDateWithTime, createJSONDateEndDay } from '@/assets/scripts/dateBuilders'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { clearObject } from '@/assets/scripts/objects'
import { tmxIconHandler, tmxClassesHandler } from '@/assets/scripts/mappers/operationsHistory'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestTmxList, requestTmxCurrencyList, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_REQUESTS_INTERNATIONAL)
const { getTmxList, getTmxListHasMore, getTmxCurrencyList, getTmxNumberOfProcess } = mapGetters(moduleMapper.TRANSFERS_REQUESTS_INTERNATIONAL)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)
const showDetailModal = ref(false)
const tableLoadingMore = ref(false)
const selectedTmxRequest = ref()
const showFiltersModal = ref(false)
const filterTags = ref([])
const showFilters = ref(false)

/*************************************************
*                                                *
*                  Empty States                  *
*                                                *
*************************************************/
const emptyStateIlustration = computed(() => {
    if (!filterTags.value.length) return 'emptyState/transfers-international'

    return 'emptyState/filters'
})
const emptyStateTitle = computed(() => {
    if (!filterTags.value.length) return t('transfers.requests.international.emptyState.genericTitle')

    return t('transfers.requests.international.emptyState.noFiltersTitle')
})

const emptyStateDescription = computed(() => {
    if (!filterTags.value.length) return t('transfers.requests.international.emptyState.genericDescription')

    return t('transfers.requests.international.emptyState.noFiltersDescription')
})

/************************************************
*												*
*     International Transfers Requests Data     *
*												*
************************************************/
const tableHeaderData = [
	{ label: t('transfers.requests.international.tableHeader.ref'), showInMobile: { groupWith: ['amount', 'creationDate'] }, dataAttr: 'ref', rowCellType: 'category' },
	{ label: t('transfers.requests.international.tableHeader.amount'), dataAttr: 'amount', rowCellType: 'entity' },
	{ label: t('transfers.requests.international.tableHeader.currency'), dataAttr: 'currency', rowCellType: 'description' },
	{ label: t('transfers.requests.international.tableHeader.creationDate'), dataAttr: 'creationDate', rowCellType: 'description' },
	{ label: t('transfers.requests.international.tableHeader.channel'), dataAttr: 'channel', rowCellType: 'description' },
	{ label: t('transfers.requests.international.tableHeader.statusDescription'), showInMobile: true, dataAttr: 'statusDescription', rowCellType: 'status' }
]

const tableStyles = [
	getMobileAppState.value ? null : { width: '180px', 'min-width': '180px' },
	{ 'text-align': 'end' },
	{ width: '70px', 'min-width': '70px' },
	{ width: '150px', 'min-width': '150px' },
	{ width: '150px', 'min-width': '150px' },
	{ width: '120px', 'min-width': '120px' }
]

const tableData = computed(() => {
	return getTmxList.value.map(tmx => {
		return [
			{
				type: 'title',
				value: tmx.externalId ? tmx.externalId : '-',
				dataAttr: 'category'
			},
			{
				type: 'amount',
				value: { content: tmx.amount, currency: getMobileAppState.value ? tmx.currency.externalId : null, noBalanceIcon: true },
				dataAttr: 'amount'
			},
			{
				value: tmx.currency.externalId,
				dataAttr: 'description'
			},
			{
				value: { content: getDateWithTime(tmx.creationDate), classes: 'capitalize' },
				dataAttr: 'creationDate'
			},
			{
				value: tmx.customerOperation.channel.description,
				dataAttr: 'externalId'
			},

			{
				type: 'status',
                value: {
                    text: tmx.statusDescription,
                    icon: tmxIconHandler(tmx.statusCode),
                    classes: tmxClassesHandler(tmx.statusCode)
                },
                dataAttr: 'status'
			}
		]
	})
})

const loadMoreHandler = () => {
	paginationParams.value.numberOfProcess = getTmxNumberOfProcess.value
	requestTmx()
}

const tmxClickHandler = row => {
	requestTmxDetail(getTmxList.value.find(rec => rec.externalId === row.rowData[0].value))
}

/************************************************
*												*
*                 Requests Logic                *
*												*
************************************************/
const requestTmx = async () => {
	cancelAllPendingRequests()
	paginationParams.value.numberOfProcess === 0 ? showLoader.value = true : tableLoadingMore.value = true

	applyErrorState.value = false
	showFeedbackToast.value = false

	const payload = {
		...paginationParams.value,
		...appliedFilters.value,
		endDate: createJSONDateEndDay(appliedFilters.value.endDate)
	}

	try {
		await requestTmxList(payload)
        await requestTmxCurrencyList()

        if (getTmxList.value.length) showFilters.value = true

		showLoader.value = false
		tableLoadingMore.value = false
	} catch (error) {
		applyErrorState.value = true
		errorHandler(error.response.data, requestTmx)
	}
}

const requestTmxDetail = (data) => {
	selectedTmxRequest.value = data
	showDetailModal.value = true
}

/************************************************
*												*
*                   Filters                     *
*												*
************************************************/
const paginationParams = ref({
	numberOfProcess: 0
})

const resetPaginationParams = () => {
	paginationParams.value = {
		numberOfProcess: 0
	}
}

const appliedFilters = ref({
	beginDate: '',
	endDate: '',
	currencyFilter: ''
})

const applyButtonDisabled = computed(() => {
    return !appliedFilters.value.beginDate && !appliedFilters.value.endDate
})

/* Begin Date Params */
const beginDateParams = computed(() => {
    return {
        labelText: t('transfers.requests.international.filters.beginDate'),
        placeholder: t('transfers.requests.international.placeholders.beginDate'),
        inputValue: appliedFilters.value.beginDate,
        disabledDates: { from: new Date() }
    }
})

/* End Date Params */
const endDateParams = computed(() => {
    return {
        labelText: t('transfers.requests.international.filters.endDate'),
        placeholder: t('transfers.requests.international.placeholders.endDate'),
        inputValue: appliedFilters.value.endDate,
        disabledDates: { from: new Date() }
    }
})

const applyFiltersHandler = (filters) => {
	appliedFilters.value = filters
    filterTags.value = []

    for (const filter in appliedFilters.value) {
		if (appliedFilters.value[filter]) {
            filterTags.value.push({
                id: filter,
                label: t(`transfers.requests.international.filters.${filter}`),
                text: filterTagTextHandler(filter, appliedFilters.value[filter])
            })
        } else {
			appliedFilters.value[filter] = null
		}
	}

	resetPaginationParams()
	requestTmx()
}

const filterTagTextHandler = (filter, value) => {
	switch (filter) {
		case 'beginDate':
		case 'endDate':
			return getDate(value)
		case 'currencyFilter':
			return getTmxCurrencyList.value.find(currency => currency.id === value).description
		default: return value
	}
}

const removeFilterTag = (tagId) => {
    appliedFilters.value[tagId] = null
	filterTags.value = filterTags.value.filter(filter => filter.id !== tagId)

    if (!filterTags.value.length) filterTags.value = []
    resetPaginationParams()
    requestTmx()
}

const closeAllFilters = () => {
    filterTags.value = []
    appliedFilters.value = clearObject(appliedFilters.value)
    resetPaginationParams()
    requestTmx()
}

/************************************************
*												*
*                 Error Feedback                *
*												*
************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/************************************************
*												*
*             Success/Error Handlers            *
*												*
************************************************/
const errorHandler = (response, retryFn) => {
	showOverlayLoader.value = false
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

requestTmx()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;
	width: 100%;
}
</style>
